import React from "react";
import sortBy from "lodash/sortBy";
import classNames from "classnames";
import { OnChangeValue } from "react-select";
import { useField, useFormikContext } from "formik";
import FormGroup from "shared/form/fields/formGroup";
import FormLabel from "shared/form/fields/formLabel";
import ErrorField from "shared/form/fields/errorField";
import { FieldResetWhenRemoved, getValueForReadOnlyFieldForStaticForm } from "shared/form/util";
import SelectDropdown from "shared/form/core/selectDropdown";
import { FormFieldOption, SelectDropdownFieldConfig } from "../types";
import { ReactSelectMenuPosition } from "shared/enums/ReactSelectMenuPlacement";
import FormOverwriteField from "../overwriteField";
import { isEmpty } from "../../../utils";
import { SINGLE_SELECT_OPTION_COUNT_FOR_SEARCH } from "shared/utils/constants";
import { useFormContextData } from "shared/form/context";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";
import { ValidateSingleSelectedFormOption } from "../utils/resetOptionsForInvalidSelection";

function FormSelectDropdownField({
  autoSelectSingleOption = true,
  menuPosition = ReactSelectMenuPosition.AUTO,
  onChange,
  ...props
}: SelectDropdownFieldConfig) {
  const formContextData = useFormContextData();
  const [selectDisabled, setSelectDisabled] = React.useState(false);
  const { setFieldTouched, setFieldValue, validateForm, validateOnChange } = useFormikContext();
  const [{ ...field }, meta] = useField({
    name: props.name,
    validate: props.validate
  });

  const onChangeHandler = React.useCallback(
    async (option: OnChangeValue<FormFieldOption, false>) => {
      await setFieldValue(props.name, option?.value, false);
      if (!meta.touched) {
        await setFieldTouched(props.name, true, false);
      }
      validateOnChange && (await validateForm());
      onChange?.(option as FormFieldOption);
    },
    [setFieldTouched, setFieldValue, props.name, meta.touched, validateForm, validateOnChange, onChange]
  );

  const getValue = React.useCallback(() => {
    if (props.options && field.value) {
      return props.options.find(option => option.value === field.value);
    }
    return null; // if not returned null here then old label will keep on displaying even after value is reset
  }, [field.value, props.options]);

  React.useEffect(() => {
    if (
      autoSelectSingleOption &&
      props.required &&
      props.options?.length === 1 &&
      !props.options?.map(opt => opt.value).includes(field.value)
    ) {
      setFieldValue(props.name, props.options[0]?.value);
      setSelectDisabled(true);
    }
  }, [
    props.name,
    field.value,
    props.options,
    setFieldValue,
    props.required,
    autoSelectSingleOption,
    setFieldTouched,
    meta.touched
  ]);

  // todo:rr refactor to use different types of sorts
  const options = React.useMemo(
    () =>
      props.sortOptionsAlphabetically
        ? sortBy(props.options, obj => (typeof obj.label === "string" ? obj.label.toLowerCase() : obj.label))
        : props.options,
    [props.options, props.sortOptionsAlphabetically]
  );

  const getConflictDisplayValue = React.useCallback(
    ({ value }) => {
      const optionObj = options.find(item => item.value === value);
      return optionObj ? optionObj.label : value;
    },
    [options]
  );

  const isSearchable = React.useMemo(() => {
    if (props.isSearchable === undefined) {
      return options.length > SINGLE_SELECT_OPTION_COUNT_FOR_SEARCH;
    }
    return props.isSearchable;
  }, [options.length, props.isSearchable]);

  return (
    <React.Fragment>
      <FormGroup className={props.className} fullWidth={props.fullWidth} indented={props.indented}>
        <FormLabel
          htmlFor={props.name}
          id={props.name}
          label={props.label}
          required={props.required}
          showTitle={props.showTitle}
          infoLabel={props.infoLabel}
          additionalLabel={props.additionalLabel}
        />
        {formContextData.isReadOnly ? (
          getValueForReadOnlyFieldForStaticForm({ renderType: FieldRenderType.SINGLE_SELECT, value: getValue()?.label })
        ) : (
          <SelectDropdown
            aria-labelledby={props.name}
            isMulti={false}
            options={options}
            name={field.name}
            value={getValue()}
            inputId={`${field.name}-input`}
            onChange={onChangeHandler}
            isDisabled={props.disabled || selectDisabled}
            menuPlacement={menuPosition}
            isClearable={props.isClearable ?? !props.required}
            placeholder={props.placeholder}
            classNamePrefix="selectDropdown"
            isSearchable={isSearchable}
            components={props.components}
            noOptionsMessage={props.noOptionsMessage}
            className={classNames("selectDropdown", props.className, {
              hasError: meta.touched && meta.error,
              isClearable: !props.required
            })}
          />
        )}
        {props.fieldDescriptiveData}
        <ErrorField meta={meta} />
        {!props.disableOptionValidation && (
          <ValidateSingleSelectedFormOption
            options={options}
            value={field.value}
            name={props.name}
            fixedInitialValueOnReset={props.fixedInitialValueOnReset}
          />
        )}
        {!isEmpty(props.conflictValue) && (
          <FormOverwriteField
            forFieldName={props.name}
            disabled={props.disabled}
            customDisplayValue={getConflictDisplayValue}
          />
        )}
        {/* todo:rr was using ResetFieldValueOnUnmount: check difference */}
        {props.resetFieldWhenRemoved && (
          <FieldResetWhenRemoved name={props.name} fixedInitialValueOnReset={props.fixedInitialValueOnReset} />
        )}
      </FormGroup>
    </React.Fragment>
  );
}

export default FormSelectDropdownField;
